import { t } from '@lingui/macro';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import { deletePage, uploadFiles } from '~/app/pages/accounting/documents/handleDocuments';
import type { Document, InboxDocument } from '~/app/pages/modules/inbox/types';
import { EntryTypeNamesEnum, type Journal } from '~/types/internal-api';
import {
    type AutoTopUpResponse,
    type CardResponse,
    CardStatus,
    type CompanyResponse,
    type Country,
    type DeliveryAddress,
    type GenericAxiosError,
    type KYCInformation,
    type LegacyDocumentUploadResponse,
    type MyntAccountInformation,
    type PaymentCardAccountResponse,
    type PaymentCardOVerviewChartData,
    type PaymentCardTodo,
    SensitiveDataOperationEnum,
    type Table3ListResponse,
    type Transaction,
    type User,
} from './types';
import { type PeriodUnit } from './common/generic-chart-card/types';

const paramObjToString = (params?: Record<string, string | number | boolean>): string => {
    if (!params || !Object.keys(params).length) {
        return '';
    }
    const urlParams = Object.entries(params).reduce((finalStr, [key, value]) => {
        if (!value && value !== 0) {
            return finalStr;
        }
        finalStr += `&${key}=${value}`;
        return finalStr;
    }, '');
    return `?${urlParams.substring(1)}`;
};

export interface CommonListParams {
    pageSize?: number;
    skipPages?: number;
    filter?: string;
    sort?: string;
    returnAll?: boolean;
}

interface OrderCardPayload {
    userId: string;
    email: string;
    phoneNumber: string;
    name: string;
    deliveryCompanyName: string;
    deliveryAddress?: DeliveryAddress;
    label?: string;
    monthlyLimit?: number;
    cardType: 'VIRTUAL' | 'PHYSICAL';
}

export interface PaymentCardsApiPayloads {
    getCards: CommonListParams;
    orderCard: OrderCardPayload;
    changeCardsStatus: {
        action: CardStatus;
        cards: string[];
    };
    updateCard: { id: string } & Partial<CardResponse>;
    getTransactions: CommonListParams;
    updateTransaction: {
        localId: number;
        note: string;
        accountNumber?: number;
    };
    attachDocumentToTransactions: {
        localId: number;
        documentId: number;
    };
    deattachDocumentToTransactions: {
        localId: number;
        documentId: number;
    };
    transferToJournal: { transactions: number[] };
    getCardPin: {
        id: string;
    };
    getCardDetails: {
        id: string;
    };
    getDocument: {
        documentNumber: number;
    };
    uploadReceipt: {
        localId: number;
        files: File[];
    };
    uploadReceiptPage: {
        files: File[];
        documentNumber: number;
    };
    deleteReceiptPage: {
        pageNumber: number;
        documentNumber?: number;
    };
    assignAccountToTransactions: {
        localIds: number[];
        accountNumber: number;
    };
    activatePhysicalCardWithToken: {
        token: string;
        localId: number;
    };
    updateCurrentCompany: CompanyResponse['model'];
    createJournal: {
        name: string;
    };
}

export interface PaymentCardUploadDocumentResponse {
    createdAt: string;
    createdByUserGuid: string;
    documentNumber: number;
    fileName: string;
    localId: number;
    setupId: number;
    transactionLocalId: number;
}

interface TransferJournalResponse {
    journalId: number;
    setupId: number;
    voucherId: number;
    transactionId: number;
}

export interface PaymentCardsApiReponses {
    changeCardsStatus: { card: CardResponse; cardId: string }[];
    getCardPin: { controlId: string; url: string };
    getCardDetails: { token: string; url: string };
    uploadReceipt: PaymentCardUploadDocumentResponse[];
    transferToJournal: TransferJournalResponse[];
    getMyntAccountInformation: MyntAccountInformation;
    getAutoTopUp: AutoTopUpResponse;
    getAccountInformation: PaymentCardAccountResponse;
    getOverviewTodoList: PaymentCardTodo[];
    getOverviewChartData: PaymentCardOVerviewChartData;
    getCurrentCompany: CompanyResponse;
    getKYC: KYCInformation;
}

export const paymentCardsApi = {
    getCards: (params?: PaymentCardsApiPayloads['getCards']) => {
        return internalApiClient.get<Table3ListResponse<CardResponse>>(
            `/paymentcard/cards${paramObjToString(params as Record<string, string | number | boolean>)}`
        );
    },
    getUsers: () => {
        return internalApiClient.get<User[]>('/users');
    },
    orderCard: (data: PaymentCardsApiPayloads['orderCard']) => {
        return internalApiClient.post<CardResponse>('/paymentcard/cards', data);
    },
    changeCardsStatus: (data: PaymentCardsApiPayloads['changeCardsStatus']) => {
        return internalApiClient.put<PaymentCardsApiReponses['changeCardsStatus']>('/paymentcard/cards', data);
    },
    updateCard: ({ id, ...data }: PaymentCardsApiPayloads['updateCard']) => {
        return internalApiClient.put<CardResponse>(`/paymentcard/card/${id}`, data);
    },
    getTransactions: (params?: PaymentCardsApiPayloads['getTransactions']) => {
        return internalApiClient.get<Table3ListResponse<Transaction>>(
            `/paymentcard/transactions${paramObjToString(params as Record<string, string | number | boolean>)}`
        );
    },
    updateTransaction: (data: PaymentCardsApiPayloads['updateTransaction']) => {
        const { localId, ...payload } = data;
        return internalApiClient.put<void>(`/paymentcard/transaction/${localId}`, payload);
    },
    attachDocumentToTransactions: (data: PaymentCardsApiPayloads['attachDocumentToTransactions']) => {
        const { localId, documentId } = data;
        return internalApiClient.put<void>(`/paymentcard/transaction/${localId}/attachfile`, documentId, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    },
    deattachDocumentToTransactions: (data: PaymentCardsApiPayloads['deattachDocumentToTransactions']) => {
        const { localId, documentId } = data;
        return internalApiClient.put<void>(`/paymentcard/transaction/${localId}/detachfile`, documentId, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    },
    transferToJournal: async (
        data: PaymentCardsApiPayloads['transferToJournal']
    ): Promise<PaymentCardsApiReponses['transferToJournal']> => {
        const response = await internalApiClient.post<PaymentCardsApiReponses['transferToJournal']>(
            `/paymentcard/transactions/addtojournal`,
            data
        );
        return response.data;
    },
    getCardPin: async (data: PaymentCardsApiPayloads['getCardPin']) => {
        const response = await internalApiClient.get<PaymentCardsApiReponses['getCardPin']>(
            `/paymentcard/sensitive?cardid=${data.id}&operation=${SensitiveDataOperationEnum.Pin}`
        );
        return response.data;
    },
    getCardDetails: async (data: PaymentCardsApiPayloads['getCardDetails']) => {
        const response = await internalApiClient.get<PaymentCardsApiReponses['getCardDetails']>(
            `/paymentcard/sensitive?cardid=${data.id}&operation=${SensitiveDataOperationEnum.Details}`
        );
        return response.data;
    },

    getCurrentCompany: async (): Promise<PaymentCardsApiReponses['getCurrentCompany']> => {
        const response = await internalApiClient.get<PaymentCardsApiReponses['getCurrentCompany']>(`/company/getCurrentCompany`);
        return response.data;
    },
    updateCurrentCompany: async (data: PaymentCardsApiPayloads['updateCurrentCompany']) => {
        const response = await internalApiClient.post(`/company/UpdateCurrentCompany`, data);
        return response.data;
    },
    getCountries: async (): Promise<Country[]> => {
        const response = await internalApiClient.get<Country[]>(`/paymentcard/countries`);
        return response.data;
    },

    getDocument: async (data: PaymentCardsApiPayloads['getDocument']): Promise<InboxDocument> => {
        const response = await internalApiClient.get<Document>(`/documents/${data.documentNumber}`);
        const doc: InboxDocument = {
            ...response.data,
            documentNumber: response.data.id,
            id: response.data.documentId,
        };
        return doc;
    },
    uploadReceipt: async (data: PaymentCardsApiPayloads['uploadReceipt']) => {
        const form = new FormData();
        const pages = data.files.splice(1);
        form.append('files', data.files[0]);
        const response = await internalApiClient.post<PaymentCardsApiReponses['uploadReceipt']>(
            `paymentcard/transactions/upload/${data.localId}`,
            form,
            {
                headers: {
                    'content-type': 'multipart/form-data',
                },
            }
        );

        const documentNumber = response.data.find(x => x?.documentNumber)?.documentNumber;
        if (pages.length > 0 && documentNumber) {
            await paymentCardsApi.uploadReceiptPage({ files: pages, documentNumber });
        }

        return documentNumber;
    },
    uploadReceiptPage: async (data: PaymentCardsApiPayloads['uploadReceiptPage']): Promise<LegacyDocumentUploadResponse> => {
        const response = await uploadFiles(data.files, undefined, data.documentNumber);
        return response.data;
    },
    deleteReceiptPage: async (data: PaymentCardsApiPayloads['deleteReceiptPage']) => {
        const { pageNumber, documentNumber } = data;
        // legacy api which currently used at other places
        await deletePage(pageNumber, documentNumber);
    },
    assignAccountToTransactions: async (data: PaymentCardsApiPayloads['assignAccountToTransactions']) => {
        await internalApiClient.patch<number[]>(`/paymentcard/transactions/bulk/accountAssign`, data);
    },
    getMyntAccountInformation: async (): Promise<PaymentCardsApiReponses['getMyntAccountInformation']> => {
        const result =
            await internalApiClient.get<PaymentCardsApiReponses['getMyntAccountInformation']>(`/paymentcard/account/depositinfo`);
        return result.data;
    },
    getAutoTopUp: async () => {
        const response = await internalApiClient.get<PaymentCardsApiReponses['getAutoTopUp']>(`/paymentcard/autotopup`);
        return response.data;
    },
    getAutoTopUpUrl: async () => {
        const response = await internalApiClient.get<string>(`/paymentcard/autotopuplink`);
        return response.data;
    },
    getTopUpUrl: async () => {
        const response = await internalApiClient.get<string>(`/paymentcard/topuplink`);
        return response.data;
    },
    getCreditURL: async () => {
        const result = await internalApiClient.get<string>(`/paymentcard/credit/url`);
        return result.data;
    },
    getKYC: async () => {
        const result = await internalApiClient.get<PaymentCardsApiReponses['getKYC']>(`/paymentcard/company/kyc`);
        return result.data;
    },
    getAccountInformation: async () => {
        const result = await internalApiClient.get<PaymentCardsApiReponses['getAccountInformation']>(`/paymentcard/account`);
        return result.data;
    },
    getOverviewChartData: async (startDate: Date, endDate: Date, unit: PeriodUnit) => {
        const payload = {
            fromDateUtc: startDate.toISOString().slice(0, 10),
            toDateUtc: endDate.toISOString().slice(0, 10),
            unit,
        };
        const params = new URLSearchParams(payload).toString();
        const result = await internalApiClient.get<PaymentCardsApiReponses['getOverviewChartData']>(
            `/paymentcard/wallet/transactionsByCards?${params}`
        );
        return result.data;
    },
    getOverviewTodoList: async () => {
        const result = await internalApiClient.get<PaymentCardsApiReponses['getOverviewTodoList']>(`/paymentcard/todos`);
        return result.data;
    },
    getMitIDStatus: async () => {
        const result = await internalApiClient.get<string>(`/paymentcard/mitid`);
        return result.data;
    },
    activatePhysicalCardWithToken: async (data: PaymentCardsApiPayloads['activatePhysicalCardWithToken']) => {
        const result = await internalApiClient.patch(`/paymentcard/card/activate/${data.localId}`, {
            activationCode: data.token,
        });
        return result.data;
    },
    activateCompany: async (): Promise<boolean> => {
        const result = await internalApiClient.post('paymentcard/integration/onboard');
        return !!result.data;
    },
    getJournals: async (): Promise<Journal[]> => {
        const result = await internalApiClient.get<Journal[]>('journals?type=classic');
        return result.data;
    },
    createJournal: async (data: PaymentCardsApiPayloads['createJournal']): Promise<boolean> => {
        const result = await internalApiClient.post('journals', {
            allowedEntryType: EntryTypeNamesEnum.All,
            name: data.name,
        });
        return !!result.data;
    },
};

const getReasons = (key?: string) => {
    if (!key) {
        return undefined;
    }
    const map: Record<string, string> = {
        ALREADY_EXISTS: t`Already exists.`,
        COMPANY_NOT_ENROLLED: t`Company not enrolled.`,
        CARD_OR_CARDS_NOT_FOUND: t`Card or cards not found.`,
        PAYMENT_CARD_FAILED: t`Company card failed.`,
        PAYMENT_CARD_FAILED_OR_SERVICE_IS_UNAVAILABLE: t`Company card failed or service is unavailable.`,
        INVALID_USER_ID: t`Invalid user id.`,
        INVALID_CARD_STATUS_OR_CARD_IDS: t`Invalid card status or card ids.`,
        ACCESS_VIOLATION: t`Access violation.`,
        COMPANY_DEACTIVATED: t`Company deactivated.`,
        ROLE_NOT_ASSIGNABLE: t`Role not assignable.`,
        PERSON_WITH_ROLE_NOT_DELETABLE: t`Person with role not deletable.`,
        INVALID_EMAIL: t`Invalid email.`,
        EMAIL_MUST_BE_UNIQUE_PER_COMPANY: t`Email must be unique per company.`,
        SSN_MUST_BE_UNIQUE_PER_COMPANY: t`SSN must be unique per company.`,
        FIRST_AND_SECOND_NAME_REQUIRED: t`First and second name required.`,
        COMPANY_NOT_FOUND: t`Company not found.`,
        PERSON_NOT_FOUND: t`Person not found.`,
        ONLY_ONE_PHYSICAL_CARD_ALLOWED: t`Only one physical card allowed.`,
        DELIVERY_COMPANY_NAME_HAS_WRONG_SIZE: t`Delivery company name has wrong size.`,
        UNAVAILABLE_FOR_PARTNER: t`Unavailable for partner.`,
        ILLEGAL_CARD_STATUS: t`Illegal card status.`,
        ERROR_ON_CREATION: t`Error on creation.`,
        INVALID_ACCOUNT_STATEMENT_PERIOD: t`Invalid account statement period.`,
        NO_ACCOUNT_STATEMENT_DATA_AVAILABLE: t`No account statement data available.`,
        NO_ACCOUNT_STATEMENT_DATA_AVAILABLE_FOR_DATE: t`No account statement data available for date.`,
        TRANSACTION_WEBHOOK_ERROR: t`Transaction webhook error.`,
        INVALID_SENT_TRANSACTIONS_PAGE_SIZE: t`Invalid sent transactions page size.`,
        INVALID_SENT_TRANSACTIONS_PAGE_INDEX: t`Invalid sent transactions page index.`,
        INVALID_SENT_TRANSACTIONS_PERIOD: t`Invalid sent transactions period.`,
    };

    return map[key];
};

export const errorHandler = (error: GenericAxiosError, fallback?: string) => {
    if (!fallback) {
        fallback = t`Something went wrong. Please try again or contact e-conomic`;
    }
    const message = getReasons(error.response?.data.reason) ?? error.response?.data?.message ?? fallback;
    return message;
};

export type PaymentCardsApi = typeof paymentCardsApi;
